import React, { useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
} from '@mui/material';
import { ArrowBack, Clear, Search } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAllActiveCheckins } from './services';

const CheckInHistory: React.FC = () => {
  const navigate = useNavigate();
  const { userData, loading: authLoading, isChildcareAdmin } = useAuth();
  const { checkins, loading } = useAllActiveCheckins(false);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return checkins;
    return checkins.filter((c) =>
      `${c.childFirstName} ${c.childLastName} ${c.parentName ?? ''}`.toLowerCase().includes(q)
    );
  }, [checkins, search]);

  if (authLoading) {
    return (
      <Container maxWidth="md" sx={{ mt: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!userData || !isChildcareAdmin) {
    return (
      <Container maxWidth="sm" sx={{ mt: 6 }}>
        <Typography variant="h6" color="error" textAlign="center">
          Access Denied
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <IconButton onClick={() => navigate('/checkin/admin')}>
          <ArrowBack />
        </IconButton>
        <Box>
          <Typography variant="h5" fontWeight="bold">
            Check-In History
          </Typography>
          <Typography variant="body2" color="text.secondary">
            All check-ins (most recent first)
          </Typography>
        </Box>
      </Stack>

      {/* Search */}
      <TextField
        fullWidth
        size="small"
        placeholder="Search by child or parent name…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: search ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setSearch('')} edge="end">
                <Clear fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
        sx={{ mb: 3 }}
      />

      {loading && (
        <Stack alignItems="center" sx={{ mt: 8 }}>
          <CircularProgress />
        </Stack>
      )}

      {!loading && filtered.length === 0 && (
        <Stack alignItems="center" spacing={2} sx={{ mt: 10 }}>
          <Typography variant="h6" color="text.secondary">
            {search ? `No results for "${search}"` : 'No check-in records found'}
          </Typography>
        </Stack>
      )}

      {!loading && filtered.length > 0 && (
        <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 700 }}>Child</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Parent</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Checked In</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Checked Out</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((record) => (
                <TableRow
                  key={record.id}
                  hover
                  sx={{ '&:last-child td': { border: 0 } }}
                >
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Avatar
                        src={record.photoUrl ?? undefined}
                        sx={{ width: 36, height: 36, fontSize: 14 }}
                      >
                        {record.childFirstName?.[0]}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {record.childFirstName} {record.childLastName}
                        </Typography>
                        {record.allergies && record.allergies !== 'N/A' && (
                          <Typography variant="caption" color="warning.dark" noWrap>
                            ⚠ {record.allergies}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {record.parentName ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {record.checkInTime.toLocaleString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap color={record.checkOutTime ? 'text.primary' : 'text.disabled'}>
                      {record.checkOutTime
                        ? record.checkOutTime.toLocaleString(undefined, {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          })
                        : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {record.checkOutTime ? (
                      <Chip label="Checked Out" size="small" color="default" />
                    ) : (
                      <Chip label="Active" size="small" color="success" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

export default CheckInHistory;
